import { Block, Value, Editor, Mark } from 'slate';
import { OrderedSet } from 'immutable';

import { BLOCK_TYPES, LIST_TYPE } from './constants';
import * as marks from './marks';
import { getNumberFromString } from '../../../../containers/Editor/utils/helpers';

export const getListType = ({ blocks, document }: Value): LIST_TYPE => {
	if (!blocks.size) {
		return LIST_TYPE.NONE;
	}

	const hasListItem = blocks.some(block => !!block && block.type === BLOCK_TYPES.li);

	if (!hasListItem) {
		return LIST_TYPE.NONE;
	}

	const parent = document.getParent(blocks.first().key);

	if (!parent) {
		return LIST_TYPE.NONE;
	}

	const listType = (parent as Block).type as BLOCK_TYPES;

	return listType === BLOCK_TYPES.ol ? LIST_TYPE.ORDERED : LIST_TYPE.UNORDERED;
};

export const addList = (editor: Editor, listType: LIST_TYPE): void => {
	const blockType = listType === LIST_TYPE.UNORDERED ? BLOCK_TYPES.ul : BLOCK_TYPES.ol;

	editor
		.setBlocks(BLOCK_TYPES.li)
		.wrapBlock(blockType);
};

export const removeList = (editor: Editor): void => {
	editor
		.setBlocks(BLOCK_TYPES.p)
		.unwrapBlock(BLOCK_TYPES.ul)
		.unwrapBlock(BLOCK_TYPES.ol);
};

export const updateList = (editor: Editor, listType: LIST_TYPE): void => {
	const prevBlockType = listType === LIST_TYPE.UNORDERED ? BLOCK_TYPES.ol : BLOCK_TYPES.ul;
	const newBlockType = listType === LIST_TYPE.UNORDERED ? BLOCK_TYPES.ul : BLOCK_TYPES.ol;

	editor
		.unwrapBlock(prevBlockType)
		.wrapBlock(newBlockType);
};

// A max font-size from list items must be set on list element (<ol> or <ul>) for correct calculation of padding-left (based on em)
const setFontSizeToListBlock = (editor: Editor, block: Block, fontSize: string): void => {
	const { key, type, data } = block;
	const newData = {
		style: {
			...data.get('style'),
			fontSize,
		},
	};

	editor.setNodeByKey(key, {
		type,
		data: newData,
	});
};

// This method is used for adding list item styles from text inside list item so list item marker has the same styles
export const addListItemsStyles = (editor: Editor): void => {
	const { value: { blocks } } = editor;
	let maxFontSize = '0px';

	blocks.forEach((block: Block, index) => {
		const { key, type, data: currentData, nodes } = block;
		const textNode = nodes.find(node => !!node && node.object === 'text');

		if (textNode) {
			const textNodeMarks = textNode.marks as OrderedSet<Mark>;

			if (textNodeMarks) {
				const fontFamily = marks.getFontFamily(textNodeMarks);
				const fontSize = marks.getFontSize(textNodeMarks);
				const fontWeight = marks.isFontWeightBold(textNodeMarks) ? 'bold' : 'normal';
				const fontStyle = marks.isFontStyleItalic(textNodeMarks) ? 'italic' : 'normal';
				const color = marks.getColor(textNodeMarks);
				const newData = {
					style: {
						...currentData.get('style'),
						fontFamily,
						fontSize,
						fontWeight,
						fontStyle,
						color,
					},
				};

				editor.setNodeByKey(key, {
					type,
					data: newData,
				});

				if (getNumberFromString(fontSize) > getNumberFromString(maxFontSize)) {
					maxFontSize = fontSize;
				}
			}
		}

		const isLastBlock = index === blocks.size - 1;

		if (isLastBlock) {
			const listBlock = editor.value.document.getParent(block.key);

			if (listBlock) {
				setFontSizeToListBlock(editor, listBlock as Block, maxFontSize);
			}
		}
	});
};

// tslint:disable-next-line: max-line-length
// This method is used for updating list item styles when some mark inside list item is changed so list item marker has the same styles as text inside list item. Styles should be updated only when all text inside list item is selected. The only way to check that is to compare selected text from each block in value.fragment (it stores all selected blocks with actual selected text inside each block) with text from corresponding block in value.blocks (it stores all selected blocks with all text inside each block)
export const updateListItemsStyles = (editor: Editor, newStyle: { [key: string]: string }): void => {
	const { value: { blocks, fragment } } = editor;

	const listItemBlocksInFragment: Block[] = [];
	fragment.nodes.forEach((node) => {
		if (node && node.object === 'block') {
			const isList = [BLOCK_TYPES.ul, BLOCK_TYPES.ol].includes(node.type as BLOCK_TYPES);

			if (isList) {
				node.nodes.forEach((childNode) => {
					if (childNode && childNode.object === 'block') {
						listItemBlocksInFragment.push(childNode);
					}
				});
			}
		}
	});

	const listItemBlocksInSelection: Block[] = [];
	blocks.forEach((block) => {
		const isListItemBlock = block && block.type === BLOCK_TYPES.li;

		if (isListItemBlock) {
			listItemBlocksInSelection.push(block as Block);
		}
	});

	const listBlockKeys: string[] = [];

	listItemBlocksInSelection.forEach((block, index) => {
		const isAllTextInBlockSelected = block.text === listItemBlocksInFragment[index].text;

		if (isAllTextInBlockSelected) {
			const { key, type, data: currentData } = block;
			const newData = {
				style: {
					...currentData.get('style'),
					...newStyle,
				},
			};

			editor.setNodeByKey(key, {
				type,
				data: newData,
			});

			const { fontSize } = newStyle;

			if (fontSize) {
				const listBlock = editor.value.document.getParent(key);

				if (listBlock && !listBlockKeys.includes(listBlock.key)) {
					listBlockKeys.push(listBlock.key);
				}
			}
		}
	});

	if (newStyle.fontSize) {
		listBlockKeys.forEach((key) => {
			const listBlock = editor.value.document.getNode(key);

			if (listBlock) {
				const { nodes: listItemBlocks } = (listBlock as Block);
				const listItemFontSizes: string[] = [];

				listItemBlocks.forEach((listItemBlock: Block) => {
					const { fontSize: listItemFontSize } = listItemBlock.data.get('style');
					listItemFontSizes.push(listItemFontSize);
				});

				listItemFontSizes.sort((a, b) => getNumberFromString(a) - getNumberFromString(b));
				const maxFontSize = listItemFontSizes[listItemFontSizes.length - 1];
				setFontSizeToListBlock(editor, listBlock as Block, maxFontSize);
			}
		});
	}
};

export const removeListItemsStyles = (editor: Editor): void => {
	const { value: { blocks } } = editor;

	blocks.forEach((block: Block) => {
		const { key, type, data } = block;
		const newStyle = { ...data.get('style') };

		[
			'fontFamily',
			'fontSize',
			'fontWeight',
			'fontStyle',
			'color',
		].forEach((style) => {
			delete newStyle[style];
		});

		editor.setNodeByKey(key, {
			type,
			data: { style: newStyle },
		});
	});
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/RichTextEditor/helpers/lists.ts